import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import Modal from './Modal'

interface TrendModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  metricName: string
  data: Array<{
    date: string
    value: number
  }>
  color: string
  unit?: string
}

export default function TrendModal({
  isOpen,
  onClose,
  title,
  metricName,
  data,
  color,
  unit = '%'
}: TrendModalProps) {
  // Calculate trend stats
  const currentValue = data[data.length - 1]?.value || 0
  const previousValue = data[0]?.value || 0
  const change = currentValue - previousValue
  const percentChange = previousValue > 0 ? (change / previousValue) * 100 : 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <Modal.Header onClose={onClose}>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-600 mt-1">6-month trend</p>
        </div>
      </Modal.Header>
      <Modal.Body>

          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm font-medium text-gray-600 mb-1">Current</div>
              <div className="text-2xl font-bold text-gray-900">
                {currentValue.toFixed(1)}{unit}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm font-medium text-gray-600 mb-1">6-Month Change</div>
              <div className={`text-2xl font-bold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? '+' : ''}{change.toFixed(1)}{unit}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm font-medium text-gray-600 mb-1">Growth Rate</div>
              <div className={`text-2xl font-bold ${percentChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={data}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => {
                    const d = new Date(date)
                    return d.toLocaleDateString('en-US', { month: 'short' })
                  }}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  label={{
                    value: metricName,
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#6B7280' }
                  }}
                />
                <Tooltip
                  formatter={(value: number) => [`${value.toFixed(1)}${unit}`, metricName]}
                  labelFormatter={(label) => {
                    const d = new Date(label)
                    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                  }}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={3}
                  dot={{ fill: color, r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

      </Modal.Body>
      <Modal.Footer className="flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  )
}
